import { atom, useAtom } from "jotai"
import { atomWithStorage } from "jotai/utils"
import { useEffect, useMemo } from "react"

import { TableViewConfig } from "@organisms/Table/Table.types"
import { migrateConfig } from "@organisms/Table/Table.utils"

import defaultTableViewConfig, { TableViewConfigType } from "@constants/defaultTableViewConfig"

const tableViewStorageKeyAtom = atom<string>("")
const tableViewConfigAtom = atomWithStorage<TableViewConfigType>("objectsView", defaultTableViewConfig, undefined, {
    getOnInit: true,
})

export default function useTableViewConfiguration(
    tableStorageKey: string | undefined,
    defaultTableConfigOverride?: TableViewConfig,
) {
    const [activeTableStorageKey, setActiveTableStorageKey] = useAtom(tableViewStorageKeyAtom)
    const [currentTableViewConfig, setTableViewConfig] = useAtom(tableViewConfigAtom)

    const resolvedStorageKey = tableStorageKey || activeTableStorageKey

    const defaultTableConfig = defaultTableConfigOverride || defaultTableViewConfig.table[resolvedStorageKey]

    const persistedTableConfig = currentTableViewConfig.table[resolvedStorageKey]

    const effectiveTableConfig = useMemo(() => {
        if (!persistedTableConfig) {
            return defaultTableConfig
        }

        // A persisted entry can predate newly-shaped defaults (or have been written from a partial
        // update), so the defaults always back-fill any keys the stored config is missing.
        const mergedTableConfig = { ...defaultTableConfig, ...persistedTableConfig }

        // Tables configured through defaultTableConfigOverride have no entry in the global
        // defaults, so the version-bump migration can never hand their persisted configs a
        // newly-added column. Appending the override's missing columns here keeps them from
        // silently vanishing for returning users; it is safe because users hide columns via
        // columnVisibility and only ever reorder columnOrder, so a missing entry can only
        // mean the persisted config predates the column.
        if (defaultTableConfigOverride) {
            const missingColumnIds = defaultTableConfigOverride.columnOrder.filter(
                (columnId) => !mergedTableConfig.columnOrder.includes(columnId),
            )
            if (missingColumnIds.length > 0) {
                mergedTableConfig.columnOrder = [...mergedTableConfig.columnOrder, ...missingColumnIds]
            }
        }

        return mergedTableConfig
    }, [persistedTableConfig, defaultTableConfig, defaultTableConfigOverride])

    useEffect(() => {
        if (!currentTableViewConfig || !defaultTableViewConfig) {
            return
        }

        const hasChanges = currentTableViewConfig.version !== defaultTableViewConfig.version

        if (hasChanges) {
            const migratedConfig = migrateConfig(currentTableViewConfig, defaultTableViewConfig)
            setTableViewConfig(migratedConfig)
        }
    }, [])

    useEffect(() => {
        if (tableStorageKey) {
            setActiveTableStorageKey(tableStorageKey)
        }
    }, [tableStorageKey])

    const updateCurrentTableViewConfig = (
        key: keyof TableViewConfig,
        updatedValue: TableViewConfig[keyof TableViewConfig],
    ) => {
        setTableViewConfig((prevViews) => ({
            ...prevViews,
            table: {
                ...prevViews.table,
                [activeTableStorageKey]: {
                    // Tables configured through defaultTableConfigOverride have no entry in the
                    // global defaults, so seeding from the override keeps the first persisted
                    // write complete instead of storing only the updated key.
                    ...(defaultTableConfigOverride ?? defaultTableViewConfig.table[activeTableStorageKey]),
                    ...(prevViews.table[activeTableStorageKey] as TableViewConfig),
                    [key]: updatedValue,
                },
            },
        }))
    }

    return {
        currentTableViewConfig: effectiveTableConfig,
        updateCurrentTableViewConfig,
    }
}
